import { Suspense, useCallback, useEffect, useMemo } from "react"
import { useUser, useWorkTask, useTelesalesCustomerNumber, useWorkTaskId, UserSettings, WorkTask } from "@tm/context-distribution"
import { Authority } from "@tm/data"
import { ILocalizationContext, useLocalization } from "@tm/localization"
import {
    encodeUniqueId,
    uniqueId,
    useDefaultErpSystem,
    useDefaultOrderWarehouse,
    useWorkTaskTruckData,
    useFetchCheckHasMemo,
    isValidWorkTaskId,
} from "@tm/utils"
import {
    RegisteredModels,
    ShowManufacturerDivisionResponse,
    ErpSystemConfig,
    SearchFilters,
    ShowDefaultWarehouseResponse,
    ErpContainer,
    AddCatalogPartListRequest,
    AddOePartListRequest,
    AddWholesalerPartListRequest,
    ChangeItemsResponse,
    AddCustomPartListRequest,
    AddCustomWorkListRequest,
    AddRepairTimeListRequest,
    FastCalculation,
    UserContext,
} from "@tm/models"
import { Container, ContainerAction, IModelContainer } from "@tm/nexus"
import { useBasketParts, useBasketWorks, useBasketImports } from "@bundles/basket"
import { match, useHistory, useRouteMatch } from "react-router"
import { History } from "history"
import { useVehicle } from "@bundles/parts/components/ListV2/hooks"
import { Theme, useTheme } from "@mui/material"
import { getBundleParams } from "../../utils"
import { handlers, PostMessageEvent } from "./handler"

type ConfigProps = {
    oeAftermarketSearchFilter?: SearchFilters
}

const BLACKLIST = ["source", "data", "type"]

function parseIfStringifiedData<T>(data: T): T | undefined {
    if (typeof data === "string") {
        try {
            return JSON.parse(data)
        } catch (ex) {
            console.warn("Postmessage API tried to parse: ", data, ex)
            return
        }
    }

    return data
}

export type PostMessageControllerProps = ConfigProps &
    WorkTask & {
        erpSystemConfig: ErpSystemConfig | undefined
        telesalesCustomerNo: string | undefined
        warehouseData: ShowDefaultWarehouseResponse | undefined
        refetchWarehouseData(): void
        workTaskTruckData: ReturnType<typeof useWorkTaskTruckData>["workTaskTruckData"]
        setWorkTaskTruckData: ReturnType<typeof useWorkTaskTruckData>["setWorkTaskTruckData"]
        getActivatableModules: ReturnType<typeof Authority.useFetchGetActivatableModules>
        getCheckHasMemo: ReturnType<typeof useFetchCheckHasMemo>
        hasTelesales: boolean
        addCustomPartList(request: AddCustomPartListRequest, isExternalCall?: boolean): Promise<ChangeItemsResponse | undefined>
        addCatalogPartList(request: AddCatalogPartListRequest): void
        addOePartList(request: AddOePartListRequest): Promise<ChangeItemsResponse | undefined>
        addWholesalerPartList(request: AddWholesalerPartListRequest): void
        addCustomWorkList(request: AddCustomWorkListRequest, isExternalCall?: boolean | undefined): void
        addRepairTimeList(request: AddRepairTimeListRequest): void
        importFastCalculation(importFastCalculationRequest: FastCalculation.ImportFastCalculationRequest): void
        getCurrentOrNewWorkTaskId(): string
        checkAndCreateWorktask(): void
        repairTimesDivision: IModelContainer<ShowManufacturerDivisionResponse, ContainerAction>
        erp: ErpContainer

        userContext: UserContext
        userSettings?: UserSettings
        history: History<unknown>
        match: match
        localization: ILocalizationContext
        theme: Theme
    }

function PostMessageControllerComponent(props: ConfigProps) {
    const { erpSystemConfig } = useDefaultErpSystem()
    const { telesalesCustomerNo, enableServiceCalls } = useTelesalesCustomerNumber()
    const { warehouseData, refetchWarehouseData } = useDefaultOrderWarehouse(
        { telesalesCustomerNo, distributorId: erpSystemConfig?.id },
        enableServiceCalls
    )
    const localization = useLocalization()
    const { userContext, userSettings } = useUser()
    const workTaskId = useWorkTaskId()
    const workTaskParams = useWorkTask()
    const theme = useTheme()
    const getActivatableModules = Authority.useFetchGetActivatableModules(userContext)

    const { workTaskTruckData, setWorkTaskTruckData } = useWorkTaskTruckData(workTaskId)

    const vehicle = useVehicle()
    const getCheckHasMemo = useFetchCheckHasMemo(
        !!getBundleParams().memoToolEnabled,
        vehicle?.manufacturer,
        vehicle?.tecDocTypeId,
        vehicle?.engineCode
    )

    const { addCustomPartList, addCatalogPartList, addOePartList, addWholesalerPartList } = useBasketParts()
    const { addCustomWorkList, addRepairTimeList } = useBasketWorks()
    const { importFastCalculation } = useBasketImports()

    const history = useHistory()
    const match = useRouteMatch()

    const getCurrentOrNewWorkTaskId = useCallback(() => {
        if (!workTaskId || !isValidWorkTaskId(workTaskId)) {
            return encodeUniqueId(uniqueId())
        }

        return workTaskId
    }, [workTaskId])

    const checkAndCreateWorktask = useCallback(() => {
        const encodedWorkTaskId = workTaskId ? encodeUniqueId(workTaskId) : workTaskId

        if (!encodedWorkTaskId || !isValidWorkTaskId(encodedWorkTaskId)) {
            history.push(`/${encodedWorkTaskId}`)
        }
    }, [workTaskId, history])

    const handlerProps = useMemo<PostMessageControllerProps>(
        () => ({
            ...props,
            repairTimesDivision: Container.getInstance<ShowManufacturerDivisionResponse>(RegisteredModels.RepairTimes_ShowManufacturerDivision),
            erp: Container.getInstance(RegisteredModels.ERP),
            hasTelesales: !!userContext.hasTelesales,
            erpSystemConfig,
            telesalesCustomerNo,
            warehouseData,
            refetchWarehouseData,
            workTaskTruckData,
            setWorkTaskTruckData,
            getActivatableModules,
            getCheckHasMemo,
            addCatalogPartList,
            addCustomPartList,
            addOePartList,
            addWholesalerPartList,
            addCustomWorkList,
            addRepairTimeList,
            importFastCalculation,
            getCurrentOrNewWorkTaskId,
            checkAndCreateWorktask,
            userContext,
            userSettings,
            history,
            match,
            localization,
            theme,
            ...workTaskParams,
        }),
        [
            addCatalogPartList,
            addCustomPartList,
            addCustomWorkList,
            addOePartList,
            addRepairTimeList,
            addWholesalerPartList,
            checkAndCreateWorktask,
            erpSystemConfig,
            getActivatableModules,
            getCheckHasMemo,
            getCurrentOrNewWorkTaskId,
            history,
            importFastCalculation,
            localization,
            match,
            props,
            refetchWarehouseData,
            setWorkTaskTruckData,
            telesalesCustomerNo,
            theme,
            userContext,
            userSettings,
            warehouseData,
            workTaskParams,
            workTaskTruckData,
        ]
    )

    const handleReceiveMessage = useCallback(
        (event: PostMessageEvent) => {
            const data = parseIfStringifiedData(event.data)
            if (!data) {
                return
            }

            const dataKeys = Object.keys(data)
            if (dataKeys.some((x) => BLACKLIST.some((y) => y === x))) {
                return
            }

            const commandKeys = dataKeys.filter((x) => x !== "sourceId")
            if (commandKeys.length === 0) {
                console.warn("API Call: No Command found.")
            }

            handlers.forEach((handle) => handle({ props: handlerProps, data, source: event.source as Window }))
        },
        [handlerProps]
    )

    useEffect(() => {
        window.addEventListener("message", handleReceiveMessage)
        return () => window.removeEventListener("message", handleReceiveMessage)
    }, [handleReceiveMessage])

    return null
}

export default function PostMessageController(props: ConfigProps) {
    return (
        <Suspense fallback={null}>
            <PostMessageControllerComponent {...props} />
        </Suspense>
    )
}
